<script setup lang="ts">
import type { VideoCardHomeFeedProps } from '.';

import { useEvents } from '~/composables/events/useEvents';
import { getSavedBandwidthEstimate } from '~/composables/useHlsPlayer/shakaLoader';
import { MixpanelProps } from '~/core/service/mixpanel';
import { formatCurrency } from '~~/utils';

import { VideoCardHomeFeedFooter } from '~/components/video-card-home-feed-footer';

import IconPilarShortsHomeFeedImage from '~icons/pilar/shorts-home-feed-image';
import IconPilarShortsHomeFeedVideo from '~icons/pilar/shorts-home-feed-video';

import { usePropertyInfo } from './usePropertyInfo';
import { useVideoCardPlayer } from './useVideoCardPlayer';
import VideoCardHeader from './VideoCardHeader.vue';
import VideoCardLoadingBar from './VideoCardLoadingBar.vue';

/**
 * Dimensões para `NuxtImg` (inteiros) e proporção do `<img>`:
 * - O card é `w-full` + altura fixa (`h-[336px]` / `lg:332` / `xl:367`)
 * - A largura de coluna útil no grid da `ShortsPage` (~4 cols em xl+ com `shortsListingPaddingX`) fica ~256 CSS px;
 *   com DPR 2, pedimos até ~512 px de largura no `srcset` (teto, não 640 “à largura”).
 * - A altura escala 2× junto para manter a mesma razão **célula** (largura_col : altura_card), coerente com `object-cover`.
 */
const HOME_FEED_GRID_COL_CSS_WIDTH = 256;
const HOME_FEED_CARD_CSS_HEIGHT = 367;
const HOME_FEED_THUMB_WIDTH = HOME_FEED_GRID_COL_CSS_WIDTH * 2;
const HOME_FEED_THUMB_HEIGHT = HOME_FEED_CARD_CSS_HEIGHT * 2;

// Reduce thumbnail quality on slow networks — WebP at 60 is visually acceptable
// and can be 2-3× smaller than quality 100, freeing bandwidth for video segments.
const thumbnailQuality = computed(() => {
	if (!import.meta.client) return 85;
	const bps = getSavedBandwidthEstimate();
	if (bps === null || bps >= 4_000_000) return 100; // fast / unknown: full quality
	if (bps >= 2_000_000) return 85; // medium
	return 70; // slow — visually acceptable, still saves ~30% vs 100
});

const props = withDefaults(defineProps<VideoCardHomeFeedProps>(), {
	isActive: false,
	imgEagerCount: 3,
	imgPreloadCount: 0,
	playerRootMargin: '100px',
	sectionName: 'home-feed',
	cardType: 'home feed card',
	layout: 'home-feed'
});

const emit = defineEmits<{
	click: [index: number];
}>();

const route = useRoute();
const property = computed(() => props.property);

const isCardHovered = ref(false);

/** Video vs image badge: há vídeo em destaque quando `featuredVideos` não está vazio. */
const showsVideoMediaBadge = computed(
	() => (props.property.featuredVideos?.length ?? 0) > 0
);

const hasVideo = computed(() => props.videoUrl != null);

const effectiveVideoUrl = computed(() =>
	hasVideo.value ? props.videoUrl! : ''
);

const {
	cardRef,
	videoRef,
	isVideoPlaying,
	isVisible,
	isLoading,
	isInitializing,
	shouldPlay
} = useVideoCardPlayer({
	videoUrl: effectiveVideoUrl,
	isActive: computed(() => props.isActive),
	hoverToPlay: computed(() => hasVideo.value),
	index: computed(() => props.index),
	playerRootMargin: props.playerRootMargin,
	isHovered: isCardHovered
});

const { propertyLocation, cityState } = usePropertyInfo(property);

/** Home feed only: `formatCurrency` (e.g. millions with M). Reels / VideoCard use `formattedPrice` from the composable. */
const formattedPriceForHomeFeed = computed(() => {
	const price = props.property.askingPrice;
	if (!price) return null;
	return formatCurrency(price) || null;
});

/** Feed has no favorite flow; header still requires the model prop. */
const isFavorite = ref(false);
const isFavoriteValid = ref(false);

const agentInfo = computed(() => props.property.agent ?? null);
const companyInfo = computed(() => props.property.company ?? null);

const { trackPropertyCardClicked } = useEvents();

const overlayVisibilityClass =
	'pointer-events-none absolute inset-0 z-10 transition-opacity duration-300 max-lg:opacity-100 lg:opacity-0 lg:group-hover:opacity-100';

const cardPath = computed(
	() => props.destinationUrl || `/shorts/${props.property.commercialId}`
);

const cardAriaLabel = computed(() =>
	props.destinationUrl
		? `Abrir detalhes do imóvel ${props.property.commercialId}`
		: `Abrir short do imóvel ${props.property.commercialId}`
);

const rootLayoutClass = computed(() =>
	props.layout === 'pdp-recommendations'
		? 'aspect-3/4 h-auto'
		: 'h-[336px] lg:h-[332px] xl:h-[367px]'
);

function isModifiedClick(event: MouseEvent): boolean {
	return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
}

function handleClick(event: MouseEvent) {
	if (!showsVideoMediaBadge.value && !(props.property.images?.length ?? 0)) {
		event.preventDefault();
		return;
	}

	trackPropertyCardClicked({
		[MixpanelProps.COMMERCIAL_ID]: props.property.commercialId,
		[MixpanelProps.DESTINATION_URL]: cardPath.value,
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.SECTION_NAME]: props.sectionName,
		[MixpanelProps.CARD_POSITION]: props.index,
		[MixpanelProps.CARD_TYPE]: props.cardType,
		[MixpanelProps.TAG_EXCLUSIVE]: props.property.isExclusive ?? false,
		[MixpanelProps.COMPANY_NAME]: props.property.company?.name ?? '',
		[MixpanelProps.AGENT_NAME]: props.property.agent?.name ?? '',
		[MixpanelProps.VIDEO_SHORTS]: hasVideo.value,
		[MixpanelProps.PHOTO_SHORTS]:
			!hasVideo.value && (props.property.images?.length ?? 0) > 0
	});

	if (isModifiedClick(event)) return;

	event.preventDefault();
	emit('click', props.index);
}
</script>

<template>
	<div
		ref="cardRef"
		:class="[
			'video-card-home-feed group relative block w-full cursor-pointer overflow-hidden bg-primary-light-4 transition-shadow duration-200',
			rootLayoutClass
		]"
		@mouseenter="isCardHovered = true"
		@mouseleave="isCardHovered = false"
	>
		<a
			:href="cardPath"
			:aria-label="cardAriaLabel"
			class="absolute inset-0 z-[5] block focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
			data-testid="home-feed-card-link"
			@click="handleClick"
		/>

		<div
			:class="[
				'absolute inset-0 transition-[transform,opacity] duration-300',
				hasVideo && isVideoPlaying
					? 'scale-105 opacity-0'
					: 'scale-100 opacity-100'
			]"
		>
			<NuxtImg
				v-if="props.thumbnailUrl"
				:src="props.thumbnailUrl"
				:alt="
					hasVideo
						? `Vídeo do imóvel ${property.commercialId}`
						: `Imóvel ${property.commercialId}`
				"
				class="size-full object-cover"
				:width="HOME_FEED_THUMB_WIDTH"
				:height="HOME_FEED_THUMB_HEIGHT"
				fit="cover"
				format="webp"
				:quality="thumbnailQuality"
				:loading="props.index < props.imgEagerCount ? 'eager' : 'lazy'"
				:preload="
					props.imgPreloadCount > 0 && props.index < props.imgPreloadCount
				"
			/>
			<div v-else class="size-full bg-primary-light-4" />
		</div>

		<video
			v-if="hasVideo && isVisible"
			ref="videoRef"
			:class="[
				'pointer-events-none absolute inset-0 size-full object-cover transition-opacity duration-300',
				isVideoPlaying ? 'opacity-100' : 'opacity-0'
			]"
			muted
			playsinline
			loop
		/>

		<div
			class="pointer-events-none absolute inset-0 z-[1] bg-[linear-gradient(180deg,_rgba(0,0,0,0.3)_11.48%,_rgba(0,0,0,0)_31.75%,_rgba(0,0,0,0)_58.79%,_rgba(0,0,0,0.3)_79.07%)]"
		/>

		<span
			class="pointer-events-none absolute right-3 top-3 z-20 inline-flex size-6 shrink-0 select-none lg:right-4 lg:top-4"
			aria-hidden="true"
		>
			<IconPilarShortsHomeFeedVideo
				v-if="showsVideoMediaBadge"
				data-testid="home-feed-media-badge-video"
				class="size-full text-white [&>svg]:size-full"
			/>
			<IconPilarShortsHomeFeedImage
				v-else
				data-testid="home-feed-media-badge-image"
				class="size-full text-white [&>svg]:size-full"
			/>
		</span>
		<div :class="overlayVisibilityClass">
			<VideoCardHeader
				v-model:is-favorite="isFavorite"
				padding-preset="home-feed"
				:agent-info="agentInfo"
				:company-info="companyInfo"
				:property-id="property.id"
				:commercial-id="property.commercialId"
				:is-muted="true"
				:is-favorite-valid="isFavoriteValid"
				:is-visible="true"
				:show-favorite-and-mute="false"
				:hide-agent-info="true"
			/>

			<VideoCardHomeFeedFooter
				:formatted-price="formattedPriceForHomeFeed"
				:region="propertyLocation"
				:city-state="cityState"
			/>
		</div>

		<!--
			Same animated strip as VideoCard; high z-index above overlay (header/footer).
			isInitializing + shouldPlay covers init without isLoading (e.g. play debounce).
		-->
		<VideoCardLoadingBar
			v-if="
				hasVideo &&
				!isVideoPlaying &&
				(isLoading || (isInitializing && shouldPlay))
			"
			class="z-[50]"
		/>
	</div>
</template>
